/**
 * FerrymanAI — pure behaviour tree for "The Ferryman" Stage 3 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Dockmaster with a chain whip. Three attack patterns:
 *   chain_whip_sweep — long-range horizontal sweep with heavy telegraph
 *   pull_in          — yanks player close (chain grab), followed by knee_strike
 *   crane_drop       — AoE shadow marker telegraphs a container falling from above
 *
 * Phase 1 (HP > 40%): round-robin chain_whip → pull_in → crane_drop
 * Phase 2 (HP ≤ 40%): same cycle, but crane_drop fires twice in sequence
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Chain whip extends well beyond normal melee range. */
export const CHAIN_WHIP_RANGE = 220;
/** Pull-in grab requires close quarters to hook the chain. */
export const PULL_RANGE = 90;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Heavy telegraph — dockmaster winds chain overhead before the sweep. */
export const CHAIN_WHIP_WINDUP_FRAMES = 24;
/** Pull windup — the chain spins before it's thrown. */
export const PULL_WINDUP_FRAMES = 16;
/** Quick knee follow-up after a successful pull. */
export const KNEE_WINDUP_FRAMES = 12;
/** Shadow marker lingers on the ground before the container crashes down. */
export const CRANE_SHADOW_FRAMES = 30;
/** Frames of backstep retreat after an attack. */
export const BACKSTEP_FRAMES = 28;
/** Speed multiplier during backstep. */
export const FERRYMAN_BACKSTEP_SPEED = 1.2;

// ── HP phase threshold ────────────────────────────────────────────────────────
export const PHASE_2_THRESHOLD = 0.4;

const BASE_COOLDOWN = 85;

// ── Types ─────────────────────────────────────────────────────────────────────
export type FerrymanPhase = 1 | 2;

export type FerrymanAttack = 'chain_whip_sweep' | 'pull_in' | 'knee_strike' | 'crane_drop';

export type FerrymanNode =
  | 'scan'
  | 'approach'
  | 'chain_whip_windup'
  | 'pull_windup'
  | 'knee_windup'
  | 'crane_shadow'
  | 'backstep';

export interface FerrymanAIState {
  node: FerrymanNode;
  phase: FerrymanPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  /** Round-robin: 0 = chain_whip_sweep, 1 = pull_in, 2 = crane_drop. */
  attackCycle: number;
  /**
   * Phase 2 only: true when the second crane drop is queued.
   * Prevents infinite double-crane loop.
   */
  pendingSecondCrane: boolean;
}

export interface FerrymanAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). */
  hpFraction: number;
}

export interface FerrymanAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: FerrymanAttack | null;
  nextState: FerrymanAIState;
}

export function initialFerrymanState(): FerrymanAIState {
  return {
    node: 'scan',
    phase: 1,
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    attackCycle: 0,
    pendingSecondCrane: false,
  };
}

/**
 * Advance the Ferryman behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns hitboxes.
 */
export function tickFerrymanAI(input: FerrymanAIInput, state: FerrymanAIState): FerrymanAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialFerrymanState());
  }

  const phase: FerrymanPhase = input.hpFraction <= PHASE_2_THRESHOLD ? 2 : 1;
  const { pendingSecondCrane } = state;

  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);

  if (input.isHitstunned) {
    return noOp(facing, {
      ...state,
      phase,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
    });
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * FERRYMAN_BACKSTEP_SPEED,
        moveY: away.y * FERRYMAN_BACKSTEP_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        nextState: { ...state, phase, pendingSecondCrane, cooldown, backstepFrames },
      };
    }
    return noOp(facing, {
      ...state,
      phase,
      pendingSecondCrane,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
    });
  }

  // ── CHAIN WHIP WINDUP ─────────────────────────────────────────────────────
  if (state.node === 'chain_whip_windup') {
    if (windup > 0) return noOp(facing, { ...state, phase, pendingSecondCrane, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'chain_whip_sweep',
      nextState: {
        ...state,
        phase,
        pendingSecondCrane,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── PULL WINDUP ───────────────────────────────────────────────────────────
  if (state.node === 'pull_windup') {
    if (windup > 0) return noOp(facing, { ...state, phase, pendingSecondCrane, cooldown, windup });
    // Pull connects — chain into knee_windup
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'pull_in',
      nextState: {
        ...state,
        phase,
        pendingSecondCrane,
        node: 'knee_windup',
        cooldown: cooldownFor(input.aggressionScale),
        windup: KNEE_WINDUP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── KNEE WINDUP (chained after pull) ─────────────────────────────────────
  if (state.node === 'knee_windup') {
    if (windup > 0) return noOp(facing, { ...state, phase, pendingSecondCrane, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'knee_strike',
      nextState: {
        ...state,
        phase,
        pendingSecondCrane,
        node: 'backstep',
        // cooldown already set at pull_in fire; knee uses remaining value
        cooldown,
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
      },
    };
  }

  // ── CRANE SHADOW (AoE telegraph) ──────────────────────────────────────────
  if (state.node === 'crane_shadow') {
    if (windup > 0) return noOp(facing, { ...state, phase, pendingSecondCrane, cooldown, windup });
    // Phase 2 double-drop: queue a second crane if this is the first
    if (phase >= 2 && !pendingSecondCrane) {
      return {
        moveX: 0,
        moveY: 0,
        facing,
        wantsAttack: true,
        attackType: 'crane_drop',
        nextState: {
          ...state,
          phase,
          pendingSecondCrane: true,
          node: 'crane_shadow',
          cooldown: cooldownFor(input.aggressionScale),
          windup: CRANE_SHADOW_FRAMES,
        },
      };
    }
    // Normal fire (phase 1) or completing the second drop (phase 2)
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'crane_drop',
      nextState: {
        ...state,
        phase,
        pendingSecondCrane: false,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);
  const { attackCycle } = state;

  if (cooldown === 0 && input.canAttack) {
    if (attackCycle === 0) {
      if (dist <= CHAIN_WHIP_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          pendingSecondCrane,
          cooldown,
          node: 'chain_whip_windup',
          windup: CHAIN_WHIP_WINDUP_FRAMES,
        });
      }
    } else if (attackCycle === 1) {
      if (dist <= PULL_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          pendingSecondCrane,
          cooldown,
          node: 'pull_windup',
          windup: PULL_WINDUP_FRAMES,
        });
      }
    } else {
      // attackCycle === 2: crane drop fires at any range
      return noOp(facing, {
        ...state,
        phase,
        pendingSecondCrane,
        cooldown,
        node: 'crane_shadow',
        windup: CRANE_SHADOW_FRAMES,
      });
    }
  }

  // ── Approach orbit position ───────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: FerrymanNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      nextState: {
        ...state,
        phase,
        pendingSecondCrane,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
      },
    };
  }

  return noOp(facing, { ...state, phase, pendingSecondCrane, cooldown, node: 'scan' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number): number {
  return Math.round(BASE_COOLDOWN * (1 - aggressionScale * 0.5));
}

function toTarget(input: FerrymanAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: FerrymanAIState): FerrymanAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    nextState,
  };
}

function nextCycle(c: number): number {
  return (c + 1) % 3;
}
